/**
 * studio/src/router.ts
 *
 * Vue Router 4 with auth guard.
 * Guards: any route with meta.requiresAuth redirects to /auth if not logged in.
 */

import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "./lib/auth";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "dashboard",
      component: () => import("./views/Dashboard.vue"),
      meta: { requiresAuth: true },
    },
    {
      // 2026-07-09 (Pitch V3 cutover, plan: pitch_v3_iterative_flow):
      // V3 is the new canonical default for /pitches/new — a 3-step
      // publish-first, iterative flow with PAS as the default framework
      // and no Stripe on first publish. The previous V2 wizard (the
      // 7-step PitchWizardV2) moved to /pitches/new-v2; see the route
      // below. The original PitchNew.vue shell is preserved at the new
      // path (mounts PitchWizardV2, zero behaviour change).
      path: "/pitches/new",
      name: "pitch-new-v3",
      component: () => import("./views/PitchNewV3.vue"),
      meta: { requiresAuth: true },
    },
    {
      // 2026-07-09 (Pitch V3 cutover): V2 wizard preserved at the
      // moved path. Mounts the existing PitchWizardV2 unchanged. All
      // internal links from sidebar / breadcrumb / post-replacement
      // CTA / Activity view etc. that previously pointed at
      // /pitches/new were audited and updated to /pitches/new-v2 in
      // the route-swap commit (CHANGELOG.d/2026-07-09-pitch-wizard-v3-route-swap.md).
      path: "/pitches/new-v2",
      name: "pitch-new-v2",
      component: () => import("./views/PitchNew.vue"),
      meta: { requiresAuth: true },
    },
    {
      // 2026-07-09 (Pitch V3 cutover, plan: pitch_v3_iterative_flow):
      // The "create a new pitch" entry-point picker. Reachable from
      // the dashboard's "+ New Pitch" button; a 2-card surface that
      // routes to V3 (recommended, the canonical `/pitches/new`
      // default) or V2 (the moved path `/pitches/new-v2`). The
      // dashboard's empty-state "Create your first pitch" CTA
      // skips the picker and goes directly to V3 — a first-time
      // creator who has no pitches doesn't need a fork decision.
      path: "/pitches/new/choose",
      name: "pitch-new-choose",
      component: () => import("./views/PitchNewPicker.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id",
      name: "pitch-edit",
      component: () => import("./views/PitchEditShell.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id/replace",
      name: "pitch-replace",
      component: () => import("./views/PitchReplace.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id/promote",
      name: "pitch-promote",
      component: () => import("./views/PitchPromote.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id/preview",
      name: "pitch-preview",
      component: () => import("./views/PitchPreview.vue"),
      meta: { requiresAuth: true },
    },
    {
      // Per-pitch view counter — studio view (phase 3 §3b).
      // Renders the per-hour view counts for one pitch by
      // calling the `read-pitch-views` Appwrite function on a
      // 60s interval.
      path: "/pitches/:id/analytics",
      name: "pitch-analytics",
      component: () => import("./views/PitchAnalytics.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/billing",
      name: "billing",
      component: () => import("./views/Billing.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/settings",
      name: "settings",
      component: () => import("./views/Settings.vue"),
      meta: { requiresAuth: true },
    },
    {
      // 2026-06-24: user-scoped account page. Distinct from
      // /settings (creator-scoped editor). The split is intentional:
      //   /account — who is logged in, what account, what sessions
      //   /settings — the creator profile, ready to publish
      // Reachable from the avatar dropdown in the top-bar; see
      // src/components/ProfileMenu.vue.
      path: "/account",
      name: "account",
      component: () => import("./views/Account.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/settings/api-keys",
      name: "settings-api-keys",
      component: () => import("./views/SettingsApiKeys.vue"),
      meta: { requiresAuth: true },
    },
    {
      // 2026-07-06: renamed from /agents/activity. The page shows
      // the full audit_log for the signed-in user, not just
      // agent work — the "Agents" filter is a tab inside the
      // view. The legacy URL is preserved as a redirect so
      // existing bookmarks and the dashboard footer continue
      // to land in the right place.
      path: "/activity",
      name: "activity",
      component: () => import("./views/ActivityView.vue"),
      meta: { requiresAuth: true },
    },
    {
      // Legacy route (2026-07-06): /agents/activity renamed to
      // /activity. Keep this redirect so bookmarks + links from
      // the old dashboard footer + any external references land
      // on the new view. The redirect route has its own name so
      // the router contract test (which forbids duplicate route
      // names) does not collide with the canonical /activity
      // route above.
      path: "/agents/activity",
      name: "agents-activity-redirect",
      redirect: { name: "activity" },
    },
    {
      // Layer 2D (2026-06-25, renamed 2026-06-25, unified 2026-07-04):
      // pitcher-side My Videos library. The path moved from
      // /library/videos to /videos as part of the URL-schema alignment.
      // 2026-07-04: collapsed into the /media tab strip — /videos is
      // now a redirect to /media?tab=videos. The route stays
      // registered (so external links + bookmarks + the redirect
      // source resolve through a known name) but MyVideosView itself
      // became a thin redirect wrapper. Pinned by
      // scripts/my-media-tabs.contract.test.mjs.
      path: "/videos",
      name: "my-videos",
      redirect: { name: "media-library", query: { tab: "videos" } },
    },
    {
      // 2026-06-25: pitcher-side My Media library. Lists every
      // `pitch_assets` row across the pitcher's pitches, with the
      // per-asset "in use on a published pitch" flag computed by
      // the Appwrite function. Locked rows render a non-dismissible
      // badge and disable the Delete button. See
      // src/views/MyMediaView.vue + src/components/ConfirmMediaDeleteModal.vue.
      path: "/media",
      name: "media-library",
      component: () => import("./views/MyMediaView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/auth",
      name: "auth",
      component: () => import("./views/Auth.vue"),
    },
    {
      // Magic-link callback — reads ?userId=...&secret=... from URL
      path: "/auth/callback",
      name: "auth-callback",
      component: () => import("./views/AuthCallback.vue"),
    },
    {
      // 2026-06-24: Intentional-error smoke route for GlitchTip /
      // Sentry verification. NOT behind the auth guard — verifying
      // error reporting must not require a sign-in. Fires three
      // events on every visit (one explicit captureException via
      // reportError, one uncaught error, one unhandled promise
      // rejection) so the operator can search GlitchTip for the
      // prefix `studio-smoke-throw:` and confirm all three Sentry
      // paths. Pinned by
      // src/__tests__/smoke-throw-route.contract.test.ts (9 cases).
      // MUST stay BEFORE the catch-all route below.
      path: "/__smoke-throw",
      name: "smoke-throw",
      component: () => import("./views/SmokeThrow.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/",
    },
  ],
});

// ─── Navigation guard ────────────────────────────────────────────────────
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Initialize auth state on first navigation
  if (!auth.initialized) {
    await auth.init();
  }

  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return { name: "auth", query: { redirect: to.fullPath } };
  }
});

export default router;
